import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, ChevronRight, Plus, X } from 'lucide-react';
import type { AaiRequest, AaiSkill } from '../../../types/aai';
import { CountUp } from '../../../components/aa';
import { askOrbitAction } from '../../../components/AskOrbit';
import { aaiApi } from '../../../services/aaiApi';
import { relativeTime } from '../../partner/format';
import { useGrow } from '../../orbit-room/OverviewCharts';
import { Card, CardLink, EASE, Legend, plural } from './primitives';

/* ── Stages ─────────────────────────────────────────────────────────── */

export type StageKey = 'asked' | 'review' | 'scoping' | 'building' | 'built' | 'live';

/* One ink ramp, light to dark, as work moves toward done. */
export const STAGES: { key: StageKey; label: string; sub: string; tone: string }[] = [
  { key: 'asked', label: 'Asked', sub: 'Waiting for a decision', tone: '#D7D8D6' },
  { key: 'review', label: 'In review', sub: 'Approved by the department', tone: '#B9BBC1' },
  { key: 'scoping', label: 'Scoping', sub: 'Orbit is shaping it', tone: '#92949D' },
  { key: 'building', label: 'Building', sub: 'PRD ready or in build', tone: '#666971' },
  { key: 'built', label: 'Built', sub: 'Built, being tested', tone: '#303238' },
  { key: 'live', label: 'Ready to use', sub: 'Delivered to the team', tone: '#18191D' },
];

const BUILDING_STATES = new Set(['PRD ready', 'Building']);
const BUILT_STATES = new Set(['Built', 'Tested']);

export const stageOf = (r: AaiRequest): StageKey => {
  if (r.status === 'pending' || r.status === 'needs_info') return 'asked';
  if (r.status === 'dept_approved') return 'review';
  if (r.tool_skill_id) return 'live';
  const state = r.display?.state || '';
  if (BUILT_STATES.has(state) || r.build_state) return 'built';
  if (BUILDING_STATES.has(state)) return 'building';
  return 'scoping';
};

type Phase = 'waiting' | 'delivery' | 'live';

const PHASES: { key: Phase; label: string; tone: string }[] = [
  { key: 'waiting', label: 'Waiting', tone: '#D7D8D6' },
  { key: 'delivery', label: 'In delivery', tone: '#92949D' },
  { key: 'live', label: 'Ready to use', tone: '#18191D' },
];

const phaseOf = (stage: StageKey): Phase => {
  if (stage === 'asked' || stage === 'review') return 'waiting';
  if (stage === 'live') return 'live';
  return 'delivery';
};

export const departmentOf = (r: AaiRequest): string => r.department || 'Whole company';

/* ── Weekly series ──────────────────────────────────────────────────── */

const DAY = 86400000;
export const WEEKS = 8;

const weekStart = (d: Date): number => {
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day.getTime();
};

export interface WeekRow { start: number; raised: number; approved: number }

/* Asks raised and asks approved, per calendar week (Monday first), oldest first. */
export const weeklySeries = (requests: AaiRequest[]): WeekRow[] => {
  const now = weekStart(new Date());
  const starts = Array.from({ length: WEEKS }, (_, i) => now - (WEEKS - 1 - i) * 7 * DAY);
  const indexOf = (iso: string | null): number => {
    if (!iso) return -1;
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return -1;
    return starts.indexOf(weekStart(at));
  };
  const rows = starts.map((start) => ({ start, raised: 0, approved: 0 }));
  requests.forEach((r) => {
    const raised = indexOf(r.created_at);
    if (raised >= 0) rows[raised].raised += 1;
    if (r.status !== 'approved') return;
    const approved = indexOf(r.decided_at);
    if (approved >= 0) rows[approved].approved += 1;
  });
  return rows;
};

const weekLabel = (start: number) => new Date(start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/* ── Pipeline ───────────────────────────────────────────────────────── */

const daysSince = (iso: string | null): number => {
  if (!iso) return 0;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.floor((Date.now() - at) / DAY));
};

const footStats = (rows: AaiRequest[]): { label: string; value: string }[] => {
  const waiting = rows.filter((r) => stageOf(r) !== 'live');
  const oldest = waiting.reduce((max, r) => Math.max(max, daysSince(r.created_at)), 0);
  const recent = rows.filter((r) => r.status === 'approved' && r.decided_at && daysSince(r.decided_at) < 30).length;
  return [
    { label: 'Need you', value: plural(rows.filter((r) => r.needs_you).length, 'project', 'projects') },
    { label: 'Oldest open', value: waiting.length ? plural(oldest, 'day', 'days') : 'None' },
    { label: 'Approved, 30 days', value: String(recent) },
  ];
};

export const Pipeline: React.FC<{
  requests: AaiRequest[];
  department: string;
  onClearDepartment: () => void;
  onOpenProject: (id: string) => void;
  onAllProjects: () => void;
}> = ({ requests, department, onClearDepartment, onOpenProject, onAllProjects }) => {
  const t = useGrow(1);
  const reduce = useReducedMotion();
  const [picked, setPicked] = useState<StageKey | ''>('');
  const scoped = department ? requests.filter((r) => departmentOf(r) === department) : requests;
  const byStage = STAGES.map((s) => {
    const rows = scoped.filter((r) => stageOf(r) === s.key);
    return { ...s, rows, needsYou: rows.filter((r) => r.needs_you).length };
  });
  const max = Math.max(1, ...byStage.map((s) => s.rows.length));
  const total = scoped.length;
  const live = byStage[byStage.length - 1].rows.length;
  const active = byStage.find((s) => s.key === picked) || null;
  const shown = active ? [...active.rows].sort((a, b) => Number(Boolean(b.needs_you)) - Number(Boolean(a.needs_you))) : [];

  let sub = `${plural(total - live, 'project', 'projects')} in flight, ${live} delivered`;
  if (department) sub = `${plural(total, 'project', 'projects')} for ${department}`;

  return (
    <Card
      id="owner-pipeline"
      title="Project pipeline"
      sub={sub}
      action={(
        <div className="flex items-center gap-2">
          {department && (
            <button
              type="button"
              onClick={onClearDepartment}
              className="inline-flex h-7 items-center gap-1 rounded-full bg-ink px-2.5 text-[11.5px] font-semibold text-white transition-colors hover:bg-ink-control"
            >
              {department}
              <X size={12} aria-label="Clear department filter" />
            </button>
          )}
          <CardLink onClick={onAllProjects}>All projects <ArrowRight size={12} /></CardLink>
        </div>
      )}
    >
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-3 lg:grid-cols-6">
        {byStage.map((s, i) => {
          const on = picked === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setPicked(on ? '' : s.key)}
              aria-pressed={on}
              aria-label={`${s.label}: ${s.rows.length}${s.needsYou ? `, ${s.needsYou} need you` : ''}`}
              className={clsx(
                'group relative flex min-h-[112px] flex-col justify-between p-3.5 text-left transition-colors duration-200 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/40',
                on && 'bg-canvas',
                !on && 'bg-white hover:bg-canvas/70',
              )}
            >
              {on && <motion.span layoutId="stage-on" className="absolute inset-x-0 top-0 h-[2px] bg-ink" transition={{ duration: 0.25, ease: EASE }} />}
              <span className="flex items-center justify-between gap-1">
                <span className="truncate text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-dim">{s.label}</span>
                {i < STAGES.length - 1 && <ChevronRight size={12} className="hidden shrink-0 text-ink-soft lg:block" aria-hidden />}
              </span>
              <span className="mt-2 flex items-end justify-between gap-2">
                <b className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-ink"><CountUp value={s.rows.length} /></b>
                <span className="relative mb-0.5 h-9 w-1.5 overflow-hidden rounded-full bg-canvas-inset" aria-hidden>
                  <span className="absolute inset-x-0 bottom-0 rounded-full" style={{ height: `${(s.rows.length / max) * 100 * t}%`, background: s.tone }} />
                </span>
              </span>
              <span className="mt-2 flex min-h-[20px] items-center">
                {s.needsYou > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-1.5 py-px text-[10.5px] font-semibold text-ink ring-1 ring-accent-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-accent-700" />
                    {s.needsYou} need{s.needsYou === 1 ? 's' : ''} you
                  </span>
                )}
                {s.needsYou === 0 && <span className="truncate text-[11px] text-ink-soft">{s.sub}</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-canvas-inset" role="img" aria-label={byStage.map((s) => `${s.label} ${s.rows.length}`).join(', ')}>
        {byStage.filter((s) => s.rows.length > 0).map((s) => (
          <motion.span
            key={s.key}
            className="h-full border-r-2 border-white last:border-r-0"
            style={{ background: s.tone }}
            initial={reduce ? false : { width: 0 }}
            animate={{ width: `${(s.rows.length / Math.max(1, total)) * 100}%` }}
            transition={{ duration: 0.8, ease: EASE }}
            title={`${s.label}: ${s.rows.length}`}
          />
        ))}
      </div>

      {total === 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-canvas px-4 py-3">
          <p className="m-0 text-[12.5px] text-ink-muted">No work in flight yet. It appears here when someone asks Orbit or you confirm an Opportunity.</p>
          {askOrbitAction()}
        </div>
      )}

      <AnimatePresence initial={false}>
        {active && (
          <motion.div
            key={active.key}
            initial={reduce ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={reduce ? undefined : { opacity: 0, height: 0, transition: { duration: 0.18 } }}
            transition={{ duration: 0.28, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="mt-3 overflow-hidden rounded-xl border border-line">
              <div className="flex items-center justify-between bg-canvas px-4 py-2">
                <span className="text-[12px] font-semibold text-ink">{active.label} · {plural(active.rows.length, 'project', 'projects')}</span>
                <button type="button" onClick={() => setPicked('')} className="rounded px-1 text-[11.5px] font-semibold text-ink-muted transition-colors hover:text-ink">Close</button>
              </div>
              {shown.length === 0 && <p className="m-0 px-4 py-4 text-[12.5px] text-ink-dim">Nothing at this stage right now.</p>}
              <ul className="m-0 max-h-[264px] list-none divide-y divide-line-soft overflow-y-auto p-0">
                {shown.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => onOpenProject(r.id)} className="group grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-canvas">
                      <span className="min-w-0">
                        <span className="flex min-w-0 items-center gap-2">
                          <b className="truncate text-[13px] font-semibold text-ink" title={r.title}>{r.title}</b>
                          {r.needs_you && <span className="shrink-0 rounded-full bg-accent-100 px-1.5 py-px text-[10px] font-semibold text-ink ring-1 ring-accent-300">Needs you</span>}
                        </span>
                        <span className="mt-0.5 block truncate text-[11.5px] text-ink-dim">
                          {[departmentOf(r), r.display?.next ? `Next: ${r.display.next}` : '', relativeTime(r.created_at)].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <ArrowRight size={14} className="text-ink-soft transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-ink" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {total > 0 && <div className="min-h-4 flex-1" aria-hidden />}
      {total > 0 && (
        <dl className="m-0 grid grid-cols-3 gap-3 border-t border-line-soft pt-3.5">
          {footStats(scoped).map((f) => (
            <div key={f.label} className="min-w-0">
              <dt className="truncate text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-dim">{f.label}</dt>
              <dd className="m-0 mt-0.5 truncate text-[13px] font-semibold tabular-nums text-ink">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
};

/* ── Weekly flow ────────────────────────────────────────────────────── */

const RAISED_TONE = '#D7D8D6';
const APPROVED_TONE = '#303238';

export const WeeklyFlow: React.FC<{ weeks: WeekRow[] }> = ({ weeks }) => {
  const t = useGrow(1);
  const [hover, setHover] = useState(-1);
  const max = Math.max(1, ...weeks.map((w) => Math.max(w.raised, w.approved)));
  const raisedTotal = weeks.reduce((s, w) => s + w.raised, 0);
  const approvedTotal = weeks.reduce((s, w) => s + w.approved, 0);
  const focus = hover >= 0 ? weeks[hover] : null;

  return (
    <Card title="Weekly flow" sub="Asks raised against asks approved, last 8 weeks">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Legend items={[{ label: 'Raised', tone: RAISED_TONE, value: raisedTotal }, { label: 'Approved', tone: APPROVED_TONE, value: approvedTotal }]} />
        <span className="min-h-[16px] text-[11px] tabular-nums text-ink-dim" aria-live="polite">
          {focus ? `Week of ${weekLabel(focus.start)}: ${focus.raised} raised, ${focus.approved} approved` : 'Hover a week for its numbers'}
        </span>
      </div>
      <div className="relative min-h-[140px] flex-1" onMouseLeave={() => setHover(-1)}>
        {[0.5, 1].map((g) => (
          <span key={g} className="pointer-events-none absolute inset-x-0 border-t border-dashed border-line" style={{ bottom: `${g * 100}%` }}>
            <span className="absolute -top-2 left-0 bg-white pr-1.5 text-[10px] tabular-nums text-ink-soft">{g === 1 ? max : ''}</span>
          </span>
        ))}
        <div className="absolute inset-0 grid items-end gap-1.5 pl-5" style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}>
          {weeks.map((w, i) => (
            <button
              key={w.start}
              type="button"
              aria-label={`Week of ${weekLabel(w.start)}: ${w.raised} raised, ${w.approved} approved`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(-1)}
              className={clsx('flex h-full items-end justify-center gap-[3px] rounded-md px-0.5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30', hover === i && 'bg-canvas-inset')}
            >
              <span className="w-full max-w-[13px] rounded-t-[4px]" style={{ height: `${(w.raised / max) * 100 * t}%`, minHeight: w.raised ? 4 : 0, background: RAISED_TONE }} />
              <span className="w-full max-w-[13px] rounded-t-[4px]" style={{ height: `${(w.approved / max) * 100 * t}%`, minHeight: w.approved ? 4 : 0, background: APPROVED_TONE }} />
            </button>
          ))}
        </div>
      </div>
      <div className="mt-1.5 grid gap-1.5 pl-5" style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}>
        {weeks.map((w, i) => (
          <span key={w.start} className={clsx('truncate text-center text-[10px] tabular-nums', hover === i && 'font-semibold text-ink', hover !== i && 'text-ink-soft')}>
            {i % 2 === 1 || hover === i ? weekLabel(w.start) : ''}
          </span>
        ))}
      </div>
    </Card>
  );
};

/* ── By department ──────────────────────────────────────────────────── */

export const ByDepartment: React.FC<{ requests: AaiRequest[]; selected: string; onSelect: (name: string) => void }> = ({ requests, selected, onSelect }) => {
  const t = useGrow(1);
  const rows = useMemo(() => {
    const map = new Map<string, Record<Phase, number>>();
    requests.forEach((r) => {
      const name = departmentOf(r);
      const row = map.get(name) || { waiting: 0, delivery: 0, live: 0 };
      row[phaseOf(stageOf(r))] += 1;
      map.set(name, row);
    });
    return Array.from(map.entries())
      .map(([name, counts]) => ({ name, counts, total: counts.waiting + counts.delivery + counts.live }))
      .sort((a, b) => b.total - a.total);
  }, [requests]);
  const max = Math.max(1, ...rows.map((r) => r.total));
  const shown = rows.slice(0, 6);

  return (
    <Card title="By department" sub="Who the work is for. Pick one to filter the pipeline">
      <Legend items={PHASES.map((p) => ({ label: p.label, tone: p.tone }))} />
      {shown.length === 0 && <p className="m-0 mt-4 text-[12.5px] text-ink-dim">No work yet.</p>}
      <div className="mt-3 grid gap-1">
        {shown.map((row) => {
          const on = selected === row.name;
          return (
            <button
              key={row.name}
              type="button"
              onClick={() => onSelect(on ? '' : row.name)}
              aria-pressed={on}
              aria-label={`${row.name}: ${PHASES.map((p) => `${row.counts[p.key]} ${p.label.toLowerCase()}`).join(', ')}`}
              title={PHASES.map((p) => `${p.label}: ${row.counts[p.key]}`).join(' · ')}
              className={clsx('grid grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)_22px] items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30', on && 'bg-canvas-inset', !on && 'hover:bg-canvas')}
            >
              <span className={clsx('truncate text-[12.5px]', on && 'font-semibold text-ink', !on && 'text-ink-muted')}>{row.name}</span>
              <span className="flex h-2.5 overflow-hidden rounded-full bg-canvas-inset">
                <span className="flex h-full" style={{ width: `${(row.total / max) * 100 * t}%` }}>
                  {PHASES.filter((p) => row.counts[p.key] > 0).map((p) => (
                    <span key={p.key} className="h-full border-r-2 border-white last:border-r-0" style={{ width: `${(row.counts[p.key] / row.total) * 100}%`, background: p.tone }} />
                  ))}
                </span>
              </span>
              <b className="text-right text-[12px] font-semibold tabular-nums text-ink">{row.total}</b>
            </button>
          );
        })}
      </div>
      {rows.length > shown.length && <p className="m-0 mt-2 px-2 text-[11px] text-ink-soft">and {plural(rows.length - shown.length, 'more department', 'more departments')}</p>}
    </Card>
  );
};

/* ── Quick Wins in use ──────────────────────────────────────────────── */

const runLabel = (runs: Record<string, number | null> | null, n: number | null): string => {
  if (!runs) return '…';
  if (n === null) return '—';
  return plural(n, 'run', 'runs');
};

/* Usage is read per Quick Win, so only the first few are read. */
const USAGE_READS = 12;

/* `liveTotal` is the company's count, every team's included. The list is what
   this reader can open, which leaves out other departments' team Quick Wins. */
export const QuickWinsUse: React.FC<{ skills: AaiSkill[]; liveTotal: number; onSeeAll: () => void }> = ({ skills, liveTotal, onSeeAll }) => {
  const t = useGrow(1);
  const live = useMemo(() => skills.filter((s) => s.published).slice(0, USAGE_READS), [skills]);
  /* A Quick Win whose runs could not be read (a provider's, which this company
     does not own, or a refused read) has no number. It is never counted as 0. */
  const [runs, setRuns] = useState<Record<string, number | null> | null>(null);
  useEffect(() => {
    let alive = true;
    Promise.allSettled(live.map((s) => aaiApi.skillUsage(s.id))).then((results) => {
      if (!alive) return;
      const next: Record<string, number | null> = {};
      results.forEach((res, i) => {
        next[live[i].id] = null;
        if (res.status === 'fulfilled') next[live[i].id] = Number(res.value.data.total_uses || 0);
      });
      setRuns(next);
    });
    return () => { alive = false; };
  }, [live]);
  const runsOf = (s: AaiSkill): number | null => (runs ? runs[s.id] ?? null : null);
  const usesOf = (s: AaiSkill): number => runsOf(s) || 0;
  const tracked = live.filter((s) => runsOf(s) !== null);
  const untracked = runs ? live.length - tracked.length : 0;
  const top = [...live].sort((a, b) => usesOf(b) - usesOf(a)).slice(0, 5);
  const max = Math.max(1, ...top.map(usesOf));
  const uses = tracked.reduce((sum, s) => sum + usesOf(s), 0);
  const unused = tracked.filter((s) => usesOf(s) === 0).length;
  const visible = skills.filter((s) => s.published).length;

  return (
    <Card title="Quick Wins in use" sub="Most used first, by runs" action={<CardLink onClick={onSeeAll}>See all <ArrowRight size={12} /></CardLink>}>
      <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-line bg-line">
        {[
          { label: 'Live', value: liveTotal },
          { label: 'Runs', value: uses },
          { label: 'Not used yet', value: unused },
        ].map((k) => (
          <div key={k.label} className="bg-white px-3 py-2.5">
            <b className="block text-[18px] font-semibold leading-tight tabular-nums text-ink">{runs || k.label === 'Live' ? <CountUp value={k.value} /> : '…'}</b>
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-dim">{k.label}</span>
          </div>
        ))}
      </div>
      {top.length === 0 && <p className="m-0 mt-4 text-[12.5px] text-ink-dim">No Quick Wins are live yet.</p>}
      {untracked > 0 && (
        <p className="m-0 mt-2 text-[11px] text-ink-soft">
          {plural(untracked, 'Quick Win comes', 'Quick Wins come')} from your provider, so {untracked === 1 ? 'its runs are' : 'their runs are'} not counted here.
        </p>
      )}
      {liveTotal > visible && (
        <p className="m-0 mt-2 text-[11px] text-ink-soft">
          {plural(liveTotal - visible, 'team Quick Win is', 'team Quick Wins are')} open only to its department, so runs and the list below leave {liveTotal - visible === 1 ? 'it' : 'them'} out.
        </p>
      )}
      <div className="mt-3 grid gap-1">
        {top.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={onSeeAll}
            className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
          >
            <span className="truncate text-[12.5px] font-medium text-ink" title={s.name}>{s.name}</span>
            <span className="text-right text-[11.5px] tabular-nums text-ink-muted">{runLabel(runs, runsOf(s))}</span>
            <span className="col-span-2 h-1.5 overflow-hidden rounded-full bg-canvas-inset">
              <span className="block h-full rounded-full bg-ink-muted transition-[width] duration-700 ease-out" style={{ width: `${(usesOf(s) / max) * 100 * t}%`, minWidth: usesOf(s) ? 4 : 0 }} />
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
};

/* ── Recent activity ────────────────────────────────────────────────── */

type ActivityVerb = 'raised' | 'approved' | 'declined' | 'reviewed';

interface ActivityRow { key: string; id: string; at: string; verb: ActivityVerb; title: string; who: string }

const VERB: Record<ActivityVerb, { word: string; icon: React.ReactNode; tone: string }> = {
  raised: { word: 'raised', icon: <Plus size={11} strokeWidth={2.5} />, tone: 'bg-canvas-inset text-ink-muted' },
  approved: { word: 'approved', icon: <Check size={11} strokeWidth={2.5} />, tone: 'bg-ok-soft text-ok-dim' },
  declined: { word: 'declined', icon: <X size={11} strokeWidth={2.5} />, tone: 'bg-danger-soft text-danger' },
  reviewed: { word: 'approved for the department', icon: <Check size={11} strokeWidth={2.5} />, tone: 'bg-canvas-inset text-ink' },
};

const DECIDED_VERB: Record<string, ActivityVerb> = { approved: 'approved', declined: 'declined', dept_approved: 'reviewed' };

const nameOf = (raw: string | null | undefined): string => {
  const value = (raw || '').trim();
  if (!value) return 'Someone';
  return value.split('@')[0];
};

/* What moved most recently: asks raised and decisions taken, newest first. */
export const RecentActivity: React.FC<{ requests: AaiRequest[]; onOpenProject: (id: string) => void }> = ({ requests, onOpenProject }) => {
  const rows = useMemo(() => {
    const out: ActivityRow[] = [];
    requests.forEach((r) => {
      if (r.created_at) out.push({ key: `${r.id}:raised`, id: r.id, at: r.created_at, verb: 'raised', title: r.title, who: nameOf(r.requester_name || r.requester_email) });
      const verb = DECIDED_VERB[r.status];
      if (verb && r.decided_at) out.push({ key: `${r.id}:${verb}`, id: r.id, at: r.decided_at, verb, title: r.title, who: nameOf(r.decided_by) });
    });
    return out.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 12);
  }, [requests]);

  return (
    <Card title="Recent activity" sub="Asks raised and decisions taken, newest first" bodyClassName="min-h-0 pt-3">
      {rows.length === 0 && <p className="m-0 text-[12.5px] text-ink-dim">Nothing has moved yet.</p>}
      <ol className="relative -mr-2 m-0 min-h-0 flex-1 list-none overflow-y-auto p-0 pb-3 pr-2 [mask-image:linear-gradient(to_bottom,black_calc(100%-22px),transparent)]">
        {rows.map((row, i) => {
          const look = VERB[row.verb];
          return (
            <li key={row.key} className="relative">
              {i < rows.length - 1 && <span aria-hidden className="absolute bottom-0 left-[13px] top-7 w-px bg-line" />}
              <button type="button" onClick={() => onOpenProject(row.id)} className="group grid w-full grid-cols-[26px_minmax(0,1fr)] gap-2.5 rounded-lg py-1.5 pr-1 text-left transition-colors duration-150 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30">
                <span className={clsx('relative z-[1] mt-0.5 grid h-[22px] w-[22px] place-items-center justify-self-center rounded-full ring-2 ring-white', look.tone)}>{look.icon}</span>
                <span className="min-w-0">
                  <span className="block truncate text-[12.5px] text-ink" title={row.title}>
                    <b className="font-semibold">{row.who}</b> {look.word} <span className="text-ink-muted">{row.title}</span>
                  </span>
                  <span className="text-[11px] text-ink-soft">{relativeTime(row.at)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </Card>
  );
};
